/**
 * AnnotationCanvas Component
 * SVG overlay for drawing and viewing annotation shapes on photos
 * Uses percentage-based coordinates for resolution independence
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';

const SHAPE_COLORS = {
  pending: '#f59e0b',    // amber
  reviewed: '#304849',   // blue
  addressed: '#10b981',  // emerald
  dismissed: '#435654',  // gray
  drawing: '#ef4444'     // red for active drawing
};

export default function AnnotationCanvas({
  annotations = [],
  selectedId = null,
  onSelect = () => {},
  mode = 'view',         // 'view' | 'rectangle' | 'circle'
  onShapeComplete = () => {},
  containerRef = null,
  disabled = false
}) {
  const svgRef = useRef(null);
  const [drawing, setDrawing] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Update SVG dimensions to match container
  useEffect(() => {
    if (!containerRef?.current) return;

    const updateDimensions = () => {
      const rect = containerRef.current.getBoundingClientRect();
      setDimensions({ width: rect.width, height: rect.height });
    };

    updateDimensions();
    const observer = new ResizeObserver(updateDimensions);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [containerRef]);

  // Convert percentage coords to pixel coords
  const toPixels = useCallback((pct, axis) => {
    const size = axis === 'x' ? dimensions.width : dimensions.height;
    return pct * size;
  }, [dimensions]);

  // Convert pixel coords to percentage
  const toPercent = useCallback((px, axis) => {
    const size = axis === 'x' ? dimensions.width : dimensions.height;
    return size > 0 ? px / size : 0;
  }, [dimensions]);

  // Get mouse/touch position relative to SVG
  const getEventPosition = useCallback((e) => {
    if (!svgRef.current) return { x: 0, y: 0 };

    const rect = svgRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    return {
      x: Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (clientY - rect.top) / rect.height))
    };
  }, []);

  // Handle pointer events for drawing
  const handlePointerDown = useCallback((e) => {
    if (mode === 'view' || disabled) return;

    e.preventDefault();
    const pos = getEventPosition(e);

    setDrawing({
      type: mode,
      startX: pos.x,
      startY: pos.y,
      currentX: pos.x,
      currentY: pos.y
    });
  }, [mode, disabled, getEventPosition]);

  const handlePointerMove = useCallback((e) => {
    if (!drawing || disabled) return;

    e.preventDefault();
    const pos = getEventPosition(e);

    setDrawing(prev => ({
      ...prev,
      currentX: pos.x,
      currentY: pos.y
    }));
  }, [drawing, disabled, getEventPosition]);

  const handlePointerUp = useCallback((e) => {
    if (!drawing || disabled) return;

    e.preventDefault();
    const pos = getEventPosition(e);

    let geometry;
    if (drawing.type === 'rectangle') {
      const x = Math.min(drawing.startX, pos.x);
      const y = Math.min(drawing.startY, pos.y);
      const width = Math.abs(pos.x - drawing.startX);
      const height = Math.abs(pos.y - drawing.startY);

      // Minimum size threshold (2% of image)
      if (width < 0.02 && height < 0.02) {
        setDrawing(null);
        return;
      }

      geometry = { x, y, width, height };
    } else if (drawing.type === 'circle') {
      const cx = drawing.startX;
      const cy = drawing.startY;
      const dx = pos.x - drawing.startX;
      const dy = pos.y - drawing.startY;
      const radius = Math.sqrt(dx * dx + dy * dy);

      // Minimum size threshold
      if (radius < 0.02) {
        setDrawing(null);
        return;
      }

      geometry = { cx, cy, radius };
    }

    onShapeComplete(drawing.type, geometry);
    setDrawing(null);
  }, [drawing, disabled, getEventPosition, onShapeComplete]);

  // Render rectangle annotation
  const renderRectangle = (id, geometry, status, isSelected) => {
    const { x, y, width, height } = geometry;
    const color = SHAPE_COLORS[status] || SHAPE_COLORS.pending;

    return (
      <rect
        key={id}
        x={toPixels(x, 'x')}
        y={toPixels(y, 'y')}
        width={toPixels(width, 'x')}
        height={toPixels(height, 'y')}
        fill="transparent"
        stroke={color}
        strokeWidth={isSelected ? 3 : 2}
        strokeDasharray={status === 'dismissed' ? '5,5' : 'none'}
        className="cursor-pointer transition-opacity"
        style={{ opacity: status === 'dismissed' ? 0.5 : 1 }}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(id);
        }}
      />
    );
  };

  // Render circle annotation (true circle, not ellipse)
  const renderCircle = (id, geometry, status, isSelected) => {
    const { cx, cy, radius } = geometry;
    const color = SHAPE_COLORS[status] || SHAPE_COLORS.pending;

    // Use the smaller dimension to ensure circle fits and looks correct
    const minDimension = Math.min(dimensions.width, dimensions.height);
    const pixelRadius = radius * minDimension;

    return (
      <circle
        key={id}
        cx={toPixels(cx, 'x')}
        cy={toPixels(cy, 'y')}
        r={pixelRadius}
        fill="transparent"
        stroke={color}
        strokeWidth={isSelected ? 3 : 2}
        strokeDasharray={status === 'dismissed' ? '5,5' : 'none'}
        className="cursor-pointer transition-opacity"
        style={{ opacity: status === 'dismissed' ? 0.5 : 1 }}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(id);
        }}
      />
    );
  };

  // Render drawing preview
  const renderDrawingPreview = () => {
    if (!drawing) return null;

    if (drawing.type === 'rectangle') {
      const x = Math.min(drawing.startX, drawing.currentX);
      const y = Math.min(drawing.startY, drawing.currentY);
      const width = Math.abs(drawing.currentX - drawing.startX);
      const height = Math.abs(drawing.currentY - drawing.startY);

      return (
        <rect
          x={toPixels(x, 'x')}
          y={toPixels(y, 'y')}
          width={toPixels(width, 'x')}
          height={toPixels(height, 'y')}
          fill="rgba(239, 68, 68, 0.1)"
          stroke={SHAPE_COLORS.drawing}
          strokeWidth={2}
          strokeDasharray="5,5"
          pointerEvents="none"
        />
      );
    }

    if (drawing.type === 'circle') {
      const dx = drawing.currentX - drawing.startX;
      const dy = drawing.currentY - drawing.startY;
      const radius = Math.sqrt(dx * dx + dy * dy);
      const minDimension = Math.min(dimensions.width, dimensions.height);
      const pixelRadius = radius * minDimension;

      return (
        <circle
          cx={toPixels(drawing.startX, 'x')}
          cy={toPixels(drawing.startY, 'y')}
          r={pixelRadius}
          fill="rgba(239, 68, 68, 0.1)"
          stroke={SHAPE_COLORS.drawing}
          strokeWidth={2}
          strokeDasharray="5,5"
          pointerEvents="none"
        />
      );
    }

    return null;
  };

  return (
    <svg
      ref={svgRef}
      className="absolute inset-0 w-full h-full"
      style={{
        cursor: mode === 'view' ? 'default' : 'crosshair',
        touchAction: mode === 'view' ? 'auto' : 'none'
      }}
      onMouseDown={handlePointerDown}
      onMouseMove={handlePointerMove}
      onMouseUp={handlePointerUp}
      onMouseLeave={handlePointerUp}
      onTouchStart={handlePointerDown}
      onTouchMove={handlePointerMove}
      onTouchEnd={handlePointerUp}
      onClick={(e) => {
        if (mode === 'view') {
          e.stopPropagation();
          onSelect(null);
        }
      }}
    >
      {/* Existing annotations */}
      {annotations.map(annotation => {
        const isSelected = annotation.id === selectedId;
        if (annotation.shape_type === 'rectangle') {
          return renderRectangle(annotation.id, annotation.geometry, annotation.status, isSelected);
        }
        if (annotation.shape_type === 'circle') {
          return renderCircle(annotation.id, annotation.geometry, annotation.status, isSelected);
        }
        return null;
      })}

      {/* Drawing preview */}
      {renderDrawingPreview()}

      {/* Selection indicator */}
      {selectedId && annotations.find(a => a.id === selectedId) && (
        <g pointerEvents="none">
          {/* Small dot at center of selected shape */}
        </g>
      )}
    </svg>
  );
}
